import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiServer, FiShield, FiLock, FiSettings, FiEye, FiCheck, FiTrendingUp } = FiIcons;

const SolutionSection = () => {
    useEffect(() => {
    // SEO: Add structured data for solution section
    const solutionData = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Zardoz.ai Private Enterprise AI Deployment",
      "description": "Iron-clad, fully private, on-premise AI solutions tailored to enterprise needs with Zero-Audit architecture.",
      "provider": { "@type": "Organization", "name": "Zardoz.ai" },
      "serviceType": "Security Consulting", "areaServed": "Worldwide",
      "hasOfferCatalog": {
        "@type": "OfferCatalog", "name": "Zardoz AI Security Solutions",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "On-Premise Zardoz AI", "description": "Private AI infrastructure installed in your data center" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Zero Data Retention", "description": "No third-party storage or data retention policies" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Scalable AI Solutions", "description": "Upgradeable to latest AI models without compromising security" } }
        ]
      }
    };
    
    const scriptId = 'solution-structured-data';
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(solutionData);
  }, []);

  const features = [
    { icon: FiServer, title: "Private AI Deployment", description: "On-premise, dedicated hardware, or secure private cloud solutions." },
    { icon: FiShield, title: "Zero Third-Party Retention", description: "Your data never leaves your environment. No external storage or logging." },
    { icon: FiLock, title: "Full Encryption", description: "End-to-end encryption at rest and in motion with enterprise-grade protocols." },
    { icon: FiSettings, title: "Customized AI Tools", description: "AI solutions that operate exactly as your organization needs." },
    { icon: FiEye, title: "Zero-Trust Architecture", description: "24/7 zero-trust security with real-time threat detection and response." },
    { icon: FiCheck, title: "Compliance Ready", description: "Built to meet GDPR, HIPAA, PCI, and other regulatory requirements." },
    { icon: FiTrendingUp, title: "Always Current", description: "Local secure AI doesn't mean lagging behind. Our solution is scalable and upgradeable to the latest AI models." }
  ];

  return (
    <section className="py-20 bg-slate-900" itemScope itemType="https://schema.org/Service">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="text-center mb-16">
          <h2 className="text-4xl font-bold text-white mb-6" itemProp="name">The Zardoz Framework</h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" itemProp="description">Iron-clad, fully private AI solutions that give you complete control. No unauthorized access, no data leaks, no third-party exposure.</p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }} className="bg-slate-800 p-6 rounded-xl border border-slate-700 hover:border-teal-500 transition-colors" itemScope itemType="https://schema.org/Service">
              <meta itemProp="provider" content="Zardoz.ai" />
              <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center mb-4">
                <SafeIcon icon={feature.icon} className="text-teal-400 text-xl" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" itemProp="name">{feature.title}</h3>
              <p className="text-gray-300 leading-relaxed" itemProp="description">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;